import { useCallback, useEffect, useRef, useState } from "react";
import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";
import { apiStatus } from "utils/apiStatus";
import { handleFeatureError } from "utils/featureGate";
import { trackMixpanel } from "utils/mixpanel";
import { checkStatus } from "utils/service";
import { compareContracts } from "./ContractCompareService";
import {
  classifyError,
  failureReason,
  normaliseCompareResponse,
} from "./ContractCompareHelper";
import {
  COMPARE_ABORT_MS,
  CompareFailure,
  CompareInput,
  CompareResult,
  validateCompareInput,
} from "./ContractCompareTypesAndValidation";

const copy = strings.contractCompare;

export type CompareState =
  | { phase: "idle" }
  | { phase: "running" }
  | { phase: "result"; result: CompareResult }
  | { phase: "failed"; failure: CompareFailure };

export interface UseContractCompareOptions {
  /**
   * Called just before the 401 path signs the user out, so the page can take its leave guard off first: the logout
   * navigates, and a guard that asks "Leave this page?" there would trap an expired session (D49).
   */
  onBeforeSessionExpired?: () => void;
}

/**
 * One compare at a time: the call, its abort limit (`COMPARE_ABORT_MS`), Cancel, a busy countdown and where every failure lands.
 *
 * The result lives in this component's state, never in redux (redux is persisted to localStorage, and the text of
 * a contract is not for the disk). A run is numbered; an answer for a run that is no longer the latest -- cancelled,
 * replaced, or the page gone -- is dropped. The analytics events carry no file name, id or text (D22).
 */
export const useContractCompare = (options: UseContractCompareOptions = {}) => {
  const [state, setState] = useState<CompareState>({ phase: "idle" });
  // When the busy countdown ends (ms since the epoch), or null. `now` is re-read once a second while it runs.
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const runRef = useRef(0);
  // Set before the first await: a second press of Compare while a request is out is ignored, not sent twice.
  const runningRef = useRef(false);
  const retryAtRef = useRef<number | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const abortTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledAtRef = useRef<number | null>(null);
  const lastInputRef = useRef<CompareInput | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /** The request is over (answered or failed): its abort timer and controller are no longer needed. */
  const releaseRequest = useCallback(() => {
    if (abortTimerRef.current) clearTimeout(abortTimerRef.current);
    abortTimerRef.current = null;
    controllerRef.current = null;
  }, []);

  /** The request is still out and is being given up on (Cancel, or the page leaving). */
  const abortRequest = useCallback(() => {
    controllerRef.current?.abort();
    releaseRequest();
  }, [releaseRequest]);

  const setCountdown = useCallback((seconds: number) => {
    const at = Date.now() + seconds * 1000;
    retryAtRef.current = at;
    setRetryAt(at);
    setNow(Date.now());
  }, []);

  // Tick while a countdown runs; at zero the busy frame clears and Compare is available again.
  useEffect(() => {
    if (retryAt === null) return undefined;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= retryAt) {
        clearInterval(timer);
        retryAtRef.current = null;
        setRetryAt(null);
        setState((prev) =>
          prev.phase === "failed" && prev.failure.kind === "busy"
            ? { phase: "idle" }
            : prev,
        );
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  // The page is gone: nothing may answer into it.
  useEffect(
    () => () => {
      runRef.current += 1;
      runningRef.current = false;
      abortRequest();
    },
    [abortRequest],
  );

  const secondsLeft =
    retryAt === null ? 0 : Math.max(0, Math.ceil((retryAt - now) / 1000));

  const compare = useCallback(
    async (input: CompareInput): Promise<void> => {
      if (runningRef.current) return;
      if (retryAtRef.current !== null && retryAtRef.current > Date.now()) return;

      // The page checks before it enables Compare; this keeps the rule true for any caller.
      const problem = validateCompareInput(input);
      if (problem) {
        setState({ phase: "failed", failure: problem });
        return;
      }

      runningRef.current = true;
      const run = (runRef.current += 1);
      lastInputRef.current = input;
      const controller = new AbortController();
      controllerRef.current = controller;
      let timedOut = false;
      abortTimerRef.current = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, COMPARE_ABORT_MS);
      setState({ phase: "running" });
      trackMixpanel(copy.events.started, {
        old_side: input.old.kind === "library" ? "list" : "upload",
      });

      try {
        const raw = await compareContracts(input, controller.signal);
        if (run !== runRef.current) return;
        cancelledAtRef.current = null;
        const result = normaliseCompareResponse(raw);
        if (!result) {
          // A 200 this app cannot read is the service failing, not a result.
          trackMixpanel(copy.events.failed, { reason: "http_200" });
          setState({
            phase: "failed",
            failure: { kind: "unavailable", message: copy.unavailable },
          });
          return;
        }
        trackMixpanel(copy.events.finished, {
          status: result.status,
          changes: result.total,
          code_sentences: result.sentencesByCode,
        });
        setState({ phase: "result", result });
      } catch (error: any) {
        if (run !== runRef.current) return;
        const failure = classifyError(error, {
          timedOut,
          oldKind: input.old.kind,
          sinceCancelMs:
            cancelledAtRef.current === null
              ? null
              : Date.now() - cancelledAtRef.current,
        });
        // The service answered (or the clock ran out): a later 429 is not about a compare the user cancelled.
        cancelledAtRef.current = null;
        trackMixpanel(copy.events.failed, {
          reason: failureReason(error, { timedOut }),
        });

        if (failure.kind === "gate") {
          // Refreshes the feature list; the page keeps its own gate state until that lands.
          handleFeatureError(error);
        } else if (failure.kind === "session") {
          optionsRef.current.onBeforeSessionExpired?.();
          // `makeCall` signs the user out by itself when the body carries `status: 401` (the error page core's
          // interceptor sends) and then rewrites `message`. The `{code, message}` shape from the exception handler
          // has no `status`, so it is done here (precedent: usePresendReview).
          if (error?.message !== notifiers.LOGGEDOUT) {
            checkStatus({ status: apiStatus.unauthorized });
          }
        } else if (failure.kind === "busy") {
          setCountdown(failure.retryAfterSeconds);
        }
        setState({ phase: "failed", failure });
      } finally {
        // Only the latest run owns the shared refs: a cancelled run's late settle must not free a newer one.
        if (run === runRef.current) {
          runningRef.current = false;
          releaseRequest();
        }
      }
    },
    [releaseRequest, setCountdown],
  );

  /** Stops the call. Silent: back to the pickers with the choices kept. */
  const cancel = useCallback(() => {
    if (!runningRef.current) return;
    runRef.current += 1;
    runningRef.current = false;
    abortRequest();
    cancelledAtRef.current = Date.now();
    setState({ phase: "idle" });
  }, [abortRequest]);

  /**
   * "Compare other files", or a file changed after a failure: drops the result or the failure frame. A running
   * countdown is not cut short -- the service is still busy.
   */
  const reset = useCallback(() => {
    if (runningRef.current) return;
    setState((prev) =>
      prev.phase === "failed" && prev.failure.kind === "busy"
        ? prev
        : { phase: "idle" },
    );
  }, []);

  /** Runs the last compare again (the "Retry" of the unavailable and timed-out frames). */
  const retry = useCallback(() => {
    const last = lastInputRef.current;
    if (last) void compare(last);
  }, [compare]);

  return {
    state,
    isRunning: state.phase === "running",
    /** Seconds until Compare is available again after a busy answer; 0 when none. */
    secondsLeft,
    /** False while a compare is out or a countdown runs. */
    canCompare: state.phase !== "running" && secondsLeft === 0,
    compare,
    cancel,
    reset,
    retry,
  };
};
